import { useState } from 'react';
import gettext from '@sdk/lang/gettext';

import { LoadingTextIndicator as PageLoader } from '@web/components/LoadingTextIndicator/LoadingTextIndicator';
import { useNavigateToMessageAction } from '@web/conversations/Conversation/ConversationProvider/hooks/useNavigateToMessageAction';
import { ConversationMessages } from '@web/conversations/Conversation/ConversationMessages/ConversationMessages';

import {
  useInfiniteLoader,
  useQuickAccess,
  useScrollHandler,
} from './conversationInfiniteList.hooks';
import {
  Button,
  ItemsList,
  ItemsListWrapper,
  LoaderWrapper,
  ScrollbaleContainer,
  Wrapper,
} from './conversationInfiniteList.styles';

export const ConversationInfiniteList = () => {
  const [containerNode, setContainerNode] = useState<HTMLDivElement | null>(null);
  const { handleScroll, hasScrollLocked, saveScrollPosition, setListNode } =
    useScrollHandler(containerNode);
  const {
    loadNewer,
    loadOlder,
    hasNewerMessage,
    hasOlderMessages,
    isLoadingNewerMessages,
    isLoadingOlderMessages,
    setLoadNewerNode,
    setLoadOlderNode,
  } = useInfiniteLoader(containerNode, saveScrollPosition);

  const {
    isScrollToNewestActive,
    isScrollToUnreadActive,
    unreadMessagesCount,
    setFirstUnreadMessageAnchor,
    setNewestMessageAnchor,
  } = useQuickAccess(containerNode);

  const { navigateToFirstUnread, navigateToNewest } = useNavigateToMessageAction();

  return (
    <Wrapper>
      <Button isActive={isScrollToUnreadActive} onClick={() => navigateToFirstUnread()}>
        {unreadMessagesCount} {gettext('New messages')}
      </Button>

      <ScrollbaleContainer
        lockScroll={hasScrollLocked}
        ref={setContainerNode}
        onScroll={handleScroll}>
        <ItemsListWrapper>
          {hasOlderMessages && (
            <LoaderWrapper ref={setLoadOlderNode}>
              <PageLoader
                isPaused={!isLoadingOlderMessages}
                label={isLoadingOlderMessages ? undefined : gettext('Load older messages')}
                onClick={loadOlder}
              />
            </LoaderWrapper>
          )}

          <ItemsList ref={setListNode}>
            <ConversationMessages
              setFirstUnreadMessageAnchor={setFirstUnreadMessageAnchor}
              setNewestMessageAnchor={setNewestMessageAnchor}
            />
          </ItemsList>

          {hasNewerMessage && (
            <LoaderWrapper ref={setLoadNewerNode}>
              <PageLoader
                isPaused={!isLoadingNewerMessages}
                label={isLoadingNewerMessages ? undefined : gettext('Load newer messages')}
                onClick={loadNewer}
              />
            </LoaderWrapper>
          )}
        </ItemsListWrapper>
      </ScrollbaleContainer>

      <Button alignToBottom isActive={isScrollToNewestActive} onClick={() => navigateToNewest()}>
        {gettext('Latest message')}
      </Button>
    </Wrapper>
  );
};
